import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const DataExportSection = ({ onExport }) => {
  const [selectedData, setSelectedData] = useState({
    broadcasts: true,
    listeningHistory: true,
    comments: true,
    subscriptions: true,
    profile: true
  });
  const [exportFormat, setExportFormat] = useState('json');
  const [isExporting, setIsExporting] = useState(false);

  const dataTypes = [
    {
      key: 'broadcasts',
      label: 'Записи эфиров',
      description: 'Все ваши аудиозаписи и метаданные',
      size: '2.4 GB'
    },
    {
      key: 'listeningHistory',
      label: 'История прослушивания',
      description: 'Список прослушанных эфиров и треков',
      size: '15 MB'
    },
    {
      key: 'comments',
      label: 'Комментарии',
      description: 'Все ваши комментарии и ответы',
      size: '8 MB'
    },
    {
      key: 'subscriptions',
      label: 'Подписки',
      description: 'Список каналов на которые вы подписаны',
      size: '1 MB'
    },
    {
      key: 'profile',
      label: 'Данные профиля',
      description: 'Информация профиля и настройки',
      size: '500 KB'
    }
  ];

  const handleCheckboxChange = (key, checked) => {
    setSelectedData(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedData)?.every(v => v);
    const newState = {};
    Object.keys(selectedData)?.forEach(key => {
      newState[key] = !allSelected;
    });
    setSelectedData(newState);
  };

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      if (onExport) {
        onExport({
          selectedData,
          format: exportFormat
        });
      }
    }, 2000);
  };

  const getTotalSize = () => {
    let total = 0;
    dataTypes?.forEach(type => {
      if (selectedData?.[type?.key]) {
        const sizeStr = type?.size;
        const value = parseFloat(sizeStr);
        if (sizeStr?.includes('GB')) {
          total += value * 1024;
        } else if (sizeStr?.includes('MB')) {
          total += value;
        } else if (sizeStr?.includes('KB')) {
          total += value / 1024;
        }
      }
    });
    return total > 1024 ? `${(total / 1024)?.toFixed(1)} GB` : `${total?.toFixed(0)} MB`;
  };

  const selectedCount = Object.values(selectedData)?.filter(v => v)?.length;

  return (
    <div className="data-export-section bg-card rounded-lg p-6 shadow-soft">
      <div className="section-header flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Download" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Экспорт данных
          </h2>
          <p className="text-sm text-muted-foreground">
            Скачайте копию ваших данных
          </p>
        </div>
      </div>
      <div className="export-content space-y-6">
        <div className="export-info p-4 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-accent)" />
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">
                О экспорте данных
              </h4>
              <p className="text-xs text-muted-foreground">
                Вы можете скачать копию всех ваших данных с платформы. Процесс экспорта может занять несколько минут в зависимости от объема данных. Файл будет доступен для скачивания в течение 7 дней.
              </p>
            </div>
          </div>
        </div>

        <div className="data-selection">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-foreground">
              Выберите данные для экспорта
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
            >
              {Object.values(selectedData)?.every(v => v) ? 'Снять все' : 'Выбрать все'}
            </Button>
          </div>

          <div className="space-y-3">
            {dataTypes?.map((type) => (
              <div
                key={type?.key}
                className="data-type-item p-4 bg-background rounded-lg border border-border"
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedData?.[type?.key]}
                    onChange={(e) => handleCheckboxChange(type?.key, e?.target?.checked)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-medium text-foreground">
                        {type?.label}
                      </h4>
                      <span className="text-xs text-muted-foreground">
                        {type?.size}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {type?.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="export-format">
          <h3 className="text-base font-medium text-foreground mb-3">
            Формат экспорта
          </h3>
          <div className="flex gap-3">
            <button
              onClick={() => setExportFormat('json')}
              className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                exportFormat === 'json' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  exportFormat === 'json' ? 'bg-primary/10' : 'bg-muted'
                }`}>
                  <Icon name="FileJson" size={20} color={exportFormat === 'json' ? 'var(--color-primary)' : 'var(--color-foreground)'} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">JSON</p>
                  <p className="text-xs text-muted-foreground">Структурированный формат</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setExportFormat('zip')}
              className={`flex-1 p-4 rounded-lg border-2 transition-colors duration-200 ${
                exportFormat === 'zip' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  exportFormat === 'zip' ? 'bg-primary/10' : 'bg-muted'
                }`}>
                  <Icon name="FileArchive" size={20} color={exportFormat === 'zip' ? 'var(--color-primary)' : 'var(--color-foreground)'} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">ZIP</p>
                  <p className="text-xs text-muted-foreground">Сжатый архив</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="export-summary p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Выбрано типов данных:</span>
            <span className="text-sm font-medium text-foreground">{selectedCount} из {dataTypes?.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Общий размер:</span>
            <span className="text-sm font-medium text-foreground">{getTotalSize()}</span>
          </div>
        </div>

        <div className="form-actions flex items-center gap-3 pt-6 border-t border-border">
          <Button
            variant="default"
            onClick={handleExport}
            loading={isExporting}
            disabled={selectedCount === 0}
            iconName="Download"
            iconPosition="left"
            fullWidth
          >
            {isExporting ? 'Подготовка экспорта...' : 'Начать экспорт'}
          </Button>
        </div>

        <div className="export-warning p-4 bg-warning/10 rounded-lg border border-warning/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">
                Важная информация
              </h4>
              <p className="text-xs text-muted-foreground">
                Экспортированные данные будут содержать всю вашу личную информацию. Храните файл в безопасном месте и не делитесь им с посторонними.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExportSection;